import { isDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import classNames from 'classnames';
import { useFormAnalytics } from 'components/form-analytic-provider';
import type { IntlKey } from 'locales';
import { useCallback, useMemo, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import type { QuoteAction } from 'state/common-form/infos/infos.model';

import { ActionButton } from './ActionButton';
import type { PairedActionsButtonProps } from './PairedActionsButton.connect';
import { connectPairedActionsButton } from './PairedActionsButton.connect';
import { getDefaultLabelFromAction, getTranslationKeyForAction, isTradeAction } from './QuoteActions.helpers';

export function PairedActionsButtonComponent({
  className,
  action,
  advancedAction,
  selectedAction,
  isDisabled,
  width,
  tooltip,
  workspaceType,
  onQuoteAction,
  selectAction,
}: PairedActionsButtonProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(prevState => !prevState);

  const hasRfb = workspaceType === 'dynamic';
  const hasTooltip = isDefined(tooltip);
  const isPaired = isDefined(advancedAction);
  const isTradable = isTradeAction(action.name);
  const activeAction = useMemo(() => {
    if (selectedAction === action.name) {
      return action;
    }

    if (selectedAction === advancedAction?.name) {
      return advancedAction;
    }

    return action;
  }, [action, advancedAction, selectedAction]);

  const activeActionLabel = useActionlabel(activeAction);

  const buttonColor = useMemo(() => {
    return {
      'btn-primary': !isTradable,
      'btn-info': isTradable,
    };
  }, [isTradable]);

  const { trackSubmitForm } = useFormAnalytics();

  const onClick = useCallback(() => {
    onQuoteAction(activeAction.name);
    trackSubmitForm();
  }, [onQuoteAction, activeAction.name, trackSubmitForm]);
  const actionLabel = useActionlabel(action);
  const advancedActionLabel = useActionlabel(advancedAction);

  const handleDefaultButtonSelection = useCallback(() => {
    selectAction(action.name);
    onQuoteAction(action.name);
    trackSubmitForm();
  }, [action.name, onQuoteAction, selectAction, trackSubmitForm]);

  const handleAdvancedButtonSelection = useCallback(() => {
    if (advancedAction) {
      selectAction(advancedAction.name);
      onQuoteAction(advancedAction.name);
      trackSubmitForm();
    }
  }, [advancedAction, onQuoteAction, selectAction, trackSubmitForm]);

  const isHalfWidth = width === 'half';
  return isPaired ? (
    <>
      <div
        className={classNames('btn-group my-1', className, {
          'flex-grow-1': isHalfWidth,
        })}
      >
        <button
          data-e2e={activeAction.name + 'Button'}
          disabled={isDisabled}
          className={classNames('btn btn-xl me-0', buttonColor)}
          onClick={onClick}
        >
          <span>{activeActionLabel}</span>
          {hasRfb && hasTooltip && (
            <Tooltip
              position="top"
              contents={<FormattedMessage id={`sidePanel.actions.${tooltip}.tooltip` as IntlKey} />}
            >
              <i className="ms-2 icon">info_outline</i>
            </Tooltip>
          )}
        </button>
        <Dropdown show={dropdownOpen} onToggle={toggle} drop="up">
          <DropdownToggle
            disabled={isDisabled}
            className={classNames('btn-xl dropdown-toggle dropdown-toggle-split mx-0 pt-3 h-100', buttonColor)}
            color="primary"
          />
          <DropdownMenu renderOnMount>
            <DropdownItem as="button" onClick={handleDefaultButtonSelection}>
              {actionLabel}
            </DropdownItem>
            <DropdownItem as="button" onClick={handleAdvancedButtonSelection}>
              {advancedActionLabel}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
    </>
  ) : (
    <ActionButton
      key={action.name}
      name={action.name}
      className={classNames('my-1', className, {
        'flex-grow-1': isHalfWidth,
        'w-50': hasRfb && isHalfWidth,
      })}
      isDisabled={isDisabled}
      onQuoteAction={onQuoteAction}
      tooltip={tooltip}
    />
  );
}

export function useActionlabel(action: QuoteAction | undefined): string {
  const { formatMessage } = useIntl();
  const translationKey = useMemo(() => getTranslationKeyForAction(action), [action]);
  const isUnknownOrUndefinedButton = translationKey === 'form.buttons.Unknown';
  const labelForUnknownOrUndefinedButton = useMemo(() => getDefaultLabelFromAction(action), [action]);

  return isUnknownOrUndefinedButton
    ? labelForUnknownOrUndefinedButton
    : formatMessage({ id: translationKey, defaultMessage: labelForUnknownOrUndefinedButton });
}

export const PairedActionsButton = connectPairedActionsButton(PairedActionsButtonComponent);
